import React, { useEffect, useState } from 'react';
import { Form, Schema, Stack, Col, Grid, Row, IconButton, Divider, FormInstance, NumberInput, Checkbox } from 'rsuite';
import { useNavigate } from 'react-router-dom';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import { ObjectStorageFileCategory } from '@/models/storage.model';
import PageContent from '@/components/PageContent';
import PageNextIcon from '@rsuite/icons/PageNext';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import ErrorMessage from '@/components/ErrorMessage';
import { AxiosError } from 'axios';
import ImageUploader from '@/components/ImageUploader';
import { StoryChapter, UpsertStoryChapter } from '@/models/story-chapter.model';
import {upsertStoryChapter } from '@/services/story-chapter.service';
import Textarea from '@/components/Textarea';

const storyChapterSchema = Schema.Model<UpsertStoryChapter>({
  id: Schema.Types.NumberType(),
  name: Schema.Types.StringType().isRequired('This field is required.'),
  description: Schema.Types.StringType().isRequired('This field is required.'),
  bannerUrl: Schema.Types.StringType().isRequired('This field is required.'),
  order: Schema.Types.NumberType().isRequired('This field is required.'),
  minimumStar: Schema.Types.NumberType().isRequired('This field is required.'),
  isIntro: Schema.Types.BooleanType().isRequired('This field is required.'),
  storyId: Schema.Types.NumberType().isRequired('This field is required.'),
});


const emptyModel: UpsertStoryChapter = {
  id: undefined,
  name: '',
  description: '',
  bannerUrl: '',
  order: undefined,
  minimumStar: undefined,
  isIntro: false,
  storyId: undefined,
};

interface StoryChapterFormProps{
  formObject?: StoryChapter;
  pageState: PageState,
  storyId?: number;
};

const StoryChapterForm: React.FC<StoryChapterFormProps> = ({formObject, pageState, storyId}) => {
  const [formValue, setFormValue] = useState<UpsertStoryChapter>(emptyModel);
  const [formError, setFormError] = useState<Record<string, any>>({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  
  const nav = useNavigate();
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  useEffect(() => {
    if(pageState == PageState.New){
      setFormValue({...emptyModel, storyId: storyId});
      return;
    }

    if (formObject) {
      setFormValue(formObject);
    } else {
      setFormValue(emptyModel);
    }
  }, [formObject]);

  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) {
      console.log("errors:", formError);
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result: BaseResponseModel<StoryChapter> = await upsertStoryChapter(formValue);
      if (result.success) {
        if(pageState == PageState.New){
          nav(`/stories/chapters/${result.result.id}`, {state:{tab: 'story-chapter-tasks'}});
        } else {
          nav(`/stories/${result.result.storyId}`, {state:{tab: 'story-chapters'}});
        }
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }finally{
      setLoading(false);
    }
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      ...value
    }));
  };

  return (
    <PageContent>
      <Form name='story-chapter-form' fluid 
        ref={formRef}
        onChange={handleFormChange}
        onCheck={setFormError}
        formValue={formValue}
        model={storyChapterSchema}
        disabled={loading}
      >
        <Grid w='100%' fluid>
          <Row>
            <Col span={{xs:24, xl:8}} style={{textAlign: 'center'}}>
              <Form.Group>
                <Form.Label>Banner</Form.Label>              
                <Form.Control
                  accepter={ImageUploader}
                  name="bannerUrl"
                  height="300px"
                  url={formValue.bannerUrl}
                  objectStorageCategory={ObjectStorageFileCategory.StoryChapterBanner}
                  onChange={url => setFormValue(pre => ({...pre, bannerUrl: url}))}                
                />            
              </Form.Group>            
            </Col> 
            <Col span={{xs:24, xl:16}} >
              <Row>
                <Col span={{xs:24}} >
                  <Form.Group>
                    <Form.Label>Name</Form.Label>
                    <Form.Control name="name"/>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Description</Form.Label>
                    <Form.Control name="description" accepter={Textarea} rows={4} /> 
                  </Form.Group>
                </Col>
              </Row>
              <Row>             
                <Col span={{xs:24, lg:6}} >
                  <Form.Group>
                    <Form.Label>Order</Form.Label>
                    <Form.Control name="order" accepter={NumberInput}/>
                  </Form.Group>
                </Col>
                <Col span={{xs:24, lg:6}} >
                  <Form.Group>
                    <Form.Label>Minimum Star</Form.Label>
                    <Form.Control name="minimumStar" accepter={NumberInput}/>
                  </Form.Group>
                </Col>
                <Col span={{xs:24, lg:6}} >
                  <Form.Group>
                    <Form.Label>Is Intro</Form.Label>
                    <Form.Control name="isIntro" accepter={Checkbox} 
                      checked={!!formValue.isIntro} 
                      onChange={(val, chk) => setFormValue(pre => ({ ...pre, isIntro: chk }))}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Col>
          </Row>          
        </Grid>
      </Form>
      <Divider />
      {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}

      <Stack justifyContent="space-between">
        <IconButton icon={<PagePreviousIcon />} onClick={() => nav(-1)}>
          Back
        </IconButton>
        <IconButton
          icon={<PageNextIcon />}
          placement="right"
          appearance="primary"
          onClick={handleSubmit}
          loading={loading}
        >
          Save
        </IconButton>
      </Stack>
    </PageContent>
  );
};

export default StoryChapterForm;

